import React from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsIcon from '@mui/icons-material/Settings';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const statusChipProps = {
  1: { color: '#1976d2', bg: '#fff', border: '#1976d2' },
  2: { color: '#f57c00', bg: '#FFF8E1', border: '#f57c00' },
  3: { color: '#0288d1', bg: '#E1F5FE', border: '#0288d1' },
  4: { color: '#2e7d32', bg: '#E8F5E9', border: '#388e3c' },
  5: { color: '#c62828', bg: '#FFEBEE', border: '#ef5555' },
  6: { color: '#616161', bg: '#F5F5F5', border: '#757575' },
};

const defaultFormatDate = (value, locale) => {
  if (!value) {
    return '—';
  }

  try {
    return new Date(value).toLocaleDateString(locale);
  } catch {
    return value;
  }
};

const GuestTrackResultCard = ({ result, formatDate }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const chip = statusChipProps[result.status] || statusChipProps[1];
  const formatValueDate = formatDate || ((value) => defaultFormatDate(value, isRTL ? 'ar-SA' : 'en-GB'));

  const statusText = {
    1: t('status_new'),
    2: t('status_inProgress'),
    3: t('status_pending'),
    4: t('status_completed'),
    5: t('status_rejected'),
    6: t('status_cancelled'),
  }[result.status] || '—';

  const fields = [
    { icon: <AssignmentOutlinedIcon fontSize="small" />, label: isRTL ? 'نوع الخدمة' : 'Service', value: result.serviceName },
    { icon: <AccountCircleOutlinedIcon fontSize="small" />, label: isRTL ? 'مقدم الطلب' : 'Applicant', value: result.applicantName },
    { icon: <SettingsIcon fontSize="small" />, label: isRTL ? 'المرحلة الحالية' : 'Current Stage', value: result.stateName },
    result.activityName && { icon: <TrackChangesIcon fontSize="small" />, label: isRTL ? 'الإجراء' : 'Activity', value: result.activityName },
    { icon: <CalendarTodayIcon fontSize="small" />, label: isRTL ? 'تاريخ الطلب' : 'Date', value: formatValueDate(result.insertDate) },
    result.applicationFee != null && { icon: <MoneyIcon fontSize="small" />, label: isRTL ? 'الرسوم' : 'Fee', value: `${result.applicationFee} ${result.currencyChar3Code || ''}` },
  ].filter(Boolean);

  return (
    <Box sx={{ mt: 3, borderRadius: '16px', border: `2px solid ${chip.border}`, overflow: 'hidden' }}>
      <Box sx={{
        px: 3,
        py: 2,
        background: `linear-gradient(135deg, ${chip.bg} 0%, ${alpha(chip.bg, 0.5)} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, background: alpha(chip.color, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircleOutlineIcon sx={{ color: chip.color, fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600, lineHeight: 1 }}>
              {isRTL ? 'رقم الطلب' : 'Request No.'}
            </Typography>
            <Typography fontWeight={900} sx={{ color: chip.color, fontSize: '1.2rem' }}>{result.requestNumber}</Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip label={statusText} sx={{ bgcolor: 'transparent', color: chip.color, border: `2px solid ${chip.border}`, fontWeight: 800, borderRadius: 2.5, height: 34 }} />
          {result.stateName && (
            <Chip label={result.stateName} sx={{ bgcolor: 'transparent', color: chip.color, border: `2px solid ${chip.border}`, fontWeight: 700, borderRadius: 2.5, height: 34 }} />
          )}
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2.5, md: 3 }, bgcolor: '#fff' }}>
        <Grid container spacing={2.5}>
          {fields.map((field, index) => (
            <Grid item xs={12} sm={6} md={4} key={`${field.label}-${index}`}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>{field.icon}</Box>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9rem' }}>{field.label}:</Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>{field.value || '—'}</Typography>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ px: { xs: 2.5, md: 3 }, pb: 2.5, bgcolor: '#fff' }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate('/guest/order-details', { state: { result } })}
          sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, fontSize: '1rem' }}
        >
          {isRTL ? 'عرض تفاصيل الطلب' : 'View Full Details'}
        </Button>
      </Box>
    </Box>
  );
};

export default GuestTrackResultCard;
